// Shared icon registry for menu items.
// To add a new icon: import it here and add it to MENU_ICONS.
// Layout.js and the MenuItems admin page both read from this registry.

import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SettingsIcon from '@mui/icons-material/Settings';
import HomeIcon from '@mui/icons-material/Home';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import StarIcon from '@mui/icons-material/Star';
import GroupIcon from '@mui/icons-material/Group';
import SportsVolleyballIcon from '@mui/icons-material/SportsVolleyball';
import BarChartIcon from '@mui/icons-material/BarChart';
import PaymentIcon from '@mui/icons-material/Payment';
import LinkIcon from '@mui/icons-material/Link';
import LockOpenIcon from '@mui/icons-material/LockOpen';

// Map of icon name (string, stored in Firestore) -> MUI component.
// The string is what gets saved; the component is rendered.
export const MENU_ICONS = {
  Dashboard:        DashboardIcon,
  People:           PeopleIcon,
  CalendarMonth:    CalendarMonthIcon,
  HowToReg:         HowToRegIcon,
  ShoppingCart:     ShoppingCartIcon,
  EmojiEvents:      EmojiEventsIcon,
  Settings:         SettingsIcon,
  Home:             HomeIcon,
  AccountCircle:    AccountCircleIcon,
  Description:      DescriptionIcon,
  HelpOutline:      HelpOutlineIcon,
  Star:             StarIcon,
  Group:            GroupIcon,
  SportsVolleyball: SportsVolleyballIcon,
  BarChart:         BarChartIcon,
  Payment:          PaymentIcon,
  Link:             LinkIcon,
  LockOpen:         LockOpenIcon,
};

// Default fallback if a stored icon name isn't in the registry
export const DEFAULT_MENU_ICON = LinkIcon;

// Helper used by both Layout and the admin page
export function getMenuIcon(name) {
  return MENU_ICONS[name] || DEFAULT_MENU_ICON;
}

// All roles that can be assigned to a menu item.
// Keep in sync with the role strings stored in users.roles.
export const MENU_ROLES = [
  { value: 'admin',            label: 'Admin' },
  { value: 'coach',            label: 'Coach' },
  { value: 'assistant_coach',  label: 'Assistant Coach' },
  { value: 'tournament_admin', label: 'Tournament Admin' },
  { value: 'court_admin',      label: 'Calendar Manager' },
  { value: 'parent',           label: 'Parent' },
  { value: 'adult_athlete',    label: 'Adult Athlete' },
  { value: 'junior_athlete',   label: 'Junior Athlete' },
  { value: 'member',           label: 'Member' },
  { value: 'renter',           label: 'Renter' },
  // Contractors have a home in the app — Profile's "My Work" tab, where they
  // log hours against workEntries — but were absent from this list, so no menu
  // item could be granted to them and the one contractor with a login saw an
  // empty nav. Absent from the list is not the same as not existing.
  { value: 'contractor',       label: 'Contractor' },
  // Sees the member list and nothing else, and changes none of it. Held
  // separately from admin rather than as a weaker admin: every write control
  // on the People page is gated against this role by name, and the rules
  // refuse its writes regardless.
  { value: 'member_manager',   label: 'Member Manager' },
  // High-school coaches: the school schedule (Admin › Schools, Schedule tab),
  // read-only, every school's practices and meets.
  { value: 'hs_coach',         label: 'HS Coach' },
];

// Default menu items — used to seed Firestore on first visit and
// for the "Reset to Defaults" action. This mirrors today's hardcoded
// nav exactly: each role sees the same items they see now.
//
// Items are flagged isSystem=true so they can be reordered/role-edited/disabled
// but not deleted from the admin UI.
export const DEFAULT_MENU_ITEMS = [
  { key: 'whats_new',      label: "What's New",     path: '/dashboard',        icon: 'Dashboard',        order: 1,  enabled: true,
    roles: ['admin','coach','assistant_coach','tournament_admin','court_admin','parent','adult_athlete','junior_athlete','member','renter','contractor'],
    isSystem: true },

  { key: 'my_account_coach', label: 'My Account',   path: '/coach-dashboard',  icon: 'People',           order: 2,  enabled: true,
    roles: ['coach','assistant_coach'],
    isSystem: true },

  { key: 'my_account',     label: 'My Account',     path: '/my-account',       icon: 'People',           order: 3,  enabled: true,
    roles: ['parent','adult_athlete','junior_athlete','member','tournament_admin','renter','contractor'],
    isSystem: true },

  { key: 'people',         label: 'People',         path: '/people',           icon: 'People',           order: 4,  enabled: true,
    roles: ['admin','member_manager'],
    isSystem: true },

  { key: 'checkin',        label: 'Check-In',       path: '/checkin',          icon: 'HowToReg',         order: 5,  enabled: true,
    roles: ['admin','coach','assistant_coach','parent','adult_athlete','junior_athlete'],
    isSystem: true },

  { key: 'schedule',       label: 'Schedule',       path: '/schedule',         icon: 'CalendarMonth',    order: 6,  enabled: true,
    roles: ['admin','coach','assistant_coach','court_admin','parent','adult_athlete','junior_athlete','member','renter'],
    isSystem: true },

  { key: 'training_plans', label: 'Training Plans', path: '/training-plans',   icon: 'ShoppingCart',     order: 7,  enabled: true,
    roles: ['admin','parent','adult_athlete','junior_athlete','member'],
    isSystem: true },

  { key: 'tournaments',    label: 'Events',         path: '/events',           icon: 'EmojiEvents',      order: 8,  enabled: true,
    roles: ['admin','coach','assistant_coach','tournament_admin','parent','adult_athlete','junior_athlete','member'],
    isSystem: true },

  { key: 'tournament_admin', label: 'Tournament Admin', path: '/tournament-admin', icon: 'Settings',     order: 9,  enabled: true,
    roles: ['tournament_admin'],
    isSystem: true },

  // The overage ledger had a reader but no way in — AdminOverage was reachable
  // only by typing the URL, which is how court time sat accrued and unnoticed.
  { key: 'unbilled_court_time', label: 'Unbilled Court Time', path: '/admin/overage', icon: 'Payment', order: 10, enabled: true,
    roles: ['admin'],
    isSystem: true },
];
